import { z } from 'zod';
import { registrationChannelSchema } from '../../partner-cockpit/onboarding/registrationChannelStep.js';
import { subscriptionBillingCadenceSchema } from '../partner-subscription-management/subscriptionBillingCadence.js';
import { subscriptionPaymentMethodSchema } from '../partner-subscription-management/subscriptionPaymentMethod.js';
import { partnerCompanyTypeSchema } from '../partnerCompanyType.js';
import { teamSchema } from '../team.js';
import { validationConsolidationSchema } from './consolidation.js';
import { consolidationMergeResolutionSchema } from './consolidationMerge.js';
import { validationUidComparisonSchema, validationUidVerificationSchema } from './uidVerification.js';
import { validationDocumentTypeSchema } from './validationDocuments.js';
import { validationResidentialAddressSchema, validationWorkCategoriesSelectionSchema } from './validationTicket.js';
export const validationDocumentOutputSchema = z.object({
    id: z.number(),
    uuid: z.uuid(),
    type: validationDocumentTypeSchema,
    fileName: z.string(),
    mimeType: z.string(),
    size: z.number().nullable(),
    uploadedAt: z.date(),
});
export const validationPreviousApprovalSchema = z.object({
    ticketId: z.number(),
    decidedAt: z.date(),
    isIdentityReusable: z.boolean(),
    insuranceDocuments: z.array(validationDocumentOutputSchema),
    insuranceExpirationDate: z.date().nullable(),
    isInsuranceReusable: z.boolean(),
});
export const validationOnboardingSaleSchema = z.object({
    planName: z.string(),
    pricePerMonth: z.string(),
    paymentMethod: subscriptionPaymentMethodSchema,
    billingCadence: subscriptionBillingCadenceSchema,
    firstPayment: z.string(),
    commitmentTotal: z.string(),
    // False for applications confirmed before the record existed, which have no proof until approval.
    acceptanceRecorded: z.boolean(),
});
export const validationOnboardingApplicationSchema = z.object({
    id: z.number(),
    uuid: z.uuid(),
    companyUid: z.string().nullable(),
    companyName: z.string().nullable(),
    submittedAt: z.date(),
    districtIds: z.array(z.number()).nullable(),
    workCategories: validationWorkCategoriesSelectionSchema.nullable(),
    subscription: validationOnboardingSaleSchema.nullable(),
    registrationChannel: registrationChannelSchema.nullable(),
});
/**
 * What the partner record already holds for the fields the ticket also carries. Read-only context: a validator sees
 * what is on file and the ticket only stores a value once they decide one, the same way the company type falls through
 * to the partner's own.
 */
export const validationPartnerOnRecordSchema = z.object({
    dateOfBirth: z.date().nullable(),
    residentialAddress: validationResidentialAddressSchema.nullable(),
});
/**
 * Only what validating adds on top of a ticket. Status, assignee, partner and comments come from the ticket endpoints
 * the BackOps ticket page already loads for every type.
 */
export const validationTicketDetailsSchema = z.object({
    id: z.number(),
    uuid: z.uuid(),
    ticketId: z.number(),
    ticketUuid: z.uuid(),
    partnerId: z.number().nullable(),
    team: teamSchema,
    companyUid: z.string().nullable(),
    consolidation: validationConsolidationSchema,
    uidVerification: validationUidVerificationSchema.nullable(),
    uidComparison: validationUidComparisonSchema.nullable(),
    companyType: partnerCompanyTypeSchema.nullable(),
    dateOfBirth: z.date().nullable(),
    residentialAddress: validationResidentialAddressSchema.nullable(),
    insuranceExpirationDate: z.date().nullable(),
    // Null when the ticket has no partner yet, which is the normal case before consolidation.
    partnerOnRecord: validationPartnerOnRecordSchema.nullable(),
    onboardingApplication: validationOnboardingApplicationSchema.nullable(),
    documents: z.array(validationDocumentOutputSchema),
    previousApproval: validationPreviousApprovalSchema.nullable(),
    // Null for a validation an onboarding submission opened, which has no subscription until BackOps configures one.
    // TODO: DEV-5628 Read the terms off the `onboardingApplication` for that path instead.
    subscriptionId: z.number().nullable(),
    mergeResolution: consolidationMergeResolutionSchema.nullable(),
    decidedAt: z.date().nullable(),
    // The Old Contract section reads the full balance from its own endpoint; approval only needs the one flag.
    hasUnpaidInstallments: z.boolean(),
});
